/**
 * Launcher — mounts the time-aware hero (clock + weather share one card) +
 * beaches + events widgets above the app tile grid per mockup
 * `tmp/platform-mockups/launcher.html`.
 *
 * Data source: GET /api/v1/launcher/home (aggregated BFF with partial-failure
 * tolerance — any field may be null/empty).
 */
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { type App, getApps } from '@/services/apps';
import { getLauncherHome } from '@/services/launcher';
import { AppLauncher } from '@/components/launcher';
import LauncherHero from '@/components/launcher/LauncherHero';
import BeachesWidget from '@/components/launcher/BeachesWidget';
import EventsWidget from '@/components/launcher/EventsWidget';
import { PlatformBackground } from '@/components/platform/PlatformBackground';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/stores/authStore';

export default function Launcher() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);

  const {
    data: apps = [],
    isLoading: appsLoading,
    isError: appsError,
    refetch: refetchApps,
  } = useQuery({
    queryKey: ['apps'],
    queryFn: getApps,
  });

  const { data: home } = useQuery({
    queryKey: ['launcher-home'],
    queryFn: getLauncherHome,
    staleTime: 60_000,
  });

  const handleAppClick = useCallback(
    (app: App) => {
      if (app.status === 'active') {
        navigate(`/apps/${app.route_path}`);
      }
    },
    [navigate],
  );

  if (appsError) {
    return (
      <div className="launcher-wrap relative flex min-h-full flex-col">
        <PlatformBackground variant="launcher" />
        <div className="launcher-unavailable relative z-10 flex flex-1 flex-col items-center justify-center gap-4 p-8">
          <h2
            className="launcher-unavailable-title text-xl font-semibold"
            style={{ color: 'var(--bad, #B8353E)' }}
          >
            Platform unavailable
          </h2>
          <p className="launcher-unavailable-msg text-center">
            The platform is temporarily unavailable. Please try again.
          </p>
          <Button onClick={() => refetchApps()} className="btn-primary-platform">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!appsLoading && apps.length === 0) {
    return (
      <div className="launcher-wrap relative flex min-h-full flex-col">
        <PlatformBackground variant="launcher" />
        <div className="launcher-no-apps relative z-10 flex flex-1 flex-col items-center justify-center gap-2 p-8">
          <h2 className="launcher-no-apps-title text-xl font-semibold">No apps available</h2>
          <p className="launcher-no-apps-msg text-center">
            Contact your administrator to get access to apps.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="launcher-wrap relative flex min-h-full flex-col">
      <PlatformBackground variant="launcher" />
      <div className="launcher-content relative z-10 flex flex-1 flex-col">
        <div className="launcher-wrap-15">
          {/* Hero: greeting + one shared card (clock, date, weather) */}
          <LauncherHero
            email={home?.user.email ?? ''}
            givenName={user?.given_name ?? null}
            displayName={user?.display_name ?? null}
            weather={home?.weather ?? null}
          />

          {/* Content widgets — beaches + tonight events. Render when data present. */}
          <section aria-labelledby="launcher-widgets-heading">
            <div className="launcher-widgets-head">
              <h2 id="launcher-widgets-heading" className="launcher-widgets-head__title">
                For <em>you</em> right now
              </h2>
            </div>
            <div className="launcher-widgets">
              <BeachesWidget beaches={home?.liked_beaches ?? []} />
              <EventsWidget events={home?.tonight_events ?? []} />
            </div>
          </section>

          {/* App tile grid — AppLauncher owns its own editorial heading */}
          <AppLauncher apps={apps} loading={appsLoading} onAppClick={handleAppClick} />
        </div>
      </div>
    </div>
  );
}
